<template>
  <v-container fluid class="overflow-y-auto" style="width: 100% !important">
    <v-row class="ma-0" style="max-height: calc(100vh - 150px); overflow-y: auto">
      <v-col cols="12" class="px-0 py-0 titleLarge">
        <p class="opacity87">{{ $t('views.fobs.observation') }}</p>
      </v-col>
      <v-col cols="11" class="px-0 py-1 bodyTexts opacity87">
        <p>
          {{ '1. ' + $t('views.fobs.observationSelection') }}
        </p>
      </v-col>
      <v-col cols="11" class="px-0">
        <ZLocationInput
          v-model:latitude="markerCoordinates.latitude"
          v-model:longitude="markerCoordinates.longitude"
          :geocoder-key="googleKey"
          @geocoder-result="geocoderResult = $event">
          <v-row>
            <v-col>
              <ZLocationField
                v-model="search"
                variant="solo-filled"
                :placeholder="$t('common.concepts.searchPlaceholder')"
                flat
                rounded
                hide-details
                clearable />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" class="pl-6">
              <ZLatitudeField
                variant="outlined"
                :label="$t('domain.fields.latitude')"
                density="compact"
                hide-details
                disabled
                color="rgb(var(--v-theme-lighten))"
                heigth="100px" />
              <!-- <span> {{ latitude ? latitude.toFixed(4) : " - " }} </span> -->
            </v-col>
            <v-col cols="6" class="">
              <ZLongitudeField
                variant="outlined"
                :label="$t('domain.fields.longitude')"
                density="compact"
                hide-details
                disabled
                color="rgb(var(--v-theme-lighten))"
                heigth="100px" />
              <!-- <span> {{ longitude ? longitude.toFixed(4) : " - " }} </span> -->
            </v-col>
          </v-row>
        </ZLocationInput>
      </v-col>
      <v-col cols="10" class="px-0 pb-0 bodyTexts">
        <p>{{ '2. ' + $t('views.fobs.enter') }}</p>
      </v-col>
      <v-col cols="12" class="px-1 pt-3 pb-2">
        <!-- A tenant with a fixed date format types the day in it; everyone else keeps the native picker -->
        <v-text-field
          v-if="tenantDateFormat"
          v-model="dateText"
          type="text"
          inputmode="numeric"
          :placeholder="tenantDateFormat.toLowerCase()"
          :error="!!dateText && !date"
          variant="outlined"
          :label="$t('domain.fields.date')"
          density="compact"
          hide-details
          color="rgb(var(--v-theme-lighten))">
          <template #append-inner>
            <v-menu v-model="pickerOpen" :close-on-content-click="false">
              <template #activator="{ props: menuProps }">
                <v-btn
                  v-bind="menuProps"
                  icon="mdi-calendar"
                  variant="text"
                  size="small"
                  :aria-label="$t('common.actions.selectDate')"></v-btn>
              </template>
              <!-- Vuetify is not synced with the app language, so the calendar gets it here -->
              <v-locale-provider :locale="calendarLocale">
                <v-date-picker v-model="pickerDate" :first-day-of-week="1" @update:model-value="pickerOpen = false"></v-date-picker>
              </v-locale-provider>
            </v-menu>
          </template>
        </v-text-field>
        <v-text-field
          v-else
          v-model="dateText"
          type="date"
          variant="outlined"
          :label="$t('domain.fields.date')"
          density="compact"
          hide-details
          color="rgb(var(--v-theme-lighten))"></v-text-field>
      </v-col>
      <v-col cols="12 px-1 py-1">
        <v-text-field
          v-model="time"
          type="time"
          variant="outlined"
          :label="$t('common.time.time')"
          density="compact"
          hide-details
          color="rgb(var(--v-theme-lighten))"></v-text-field>
      </v-col>
      <v-col cols="12" class="px-0 mt-4">
        <p>{{ $t('views.fobs.spread') }}</p>
        <v-progress-circular v-if="!isDataRequested" indeterminate color="primary" class="ma-4"></v-progress-circular>
        <v-radio-group v-else v-model="surfaceSpreadModel" hide-details density="default" class="mb-1 py-1 spreadColors">
          <v-radio
            v-for="(item, i) in spreadTypes"
            :key="i"
            :label="item.displayName"
            :value="item.id"
            :disabled="item.disabled"
            :color="item.color"
            :style="{
              '--spread-radio-color': item.color
            }"
            hide-details
            class="pr-2 bodyTextsSmall">
            <template #label="{ label }">
              {{ label }}
            </template>
          </v-radio>
        </v-radio-group>
      </v-col>
      <v-col cols="12" class="px-0 mt-1">
        <p>{{ $t('views.fobs.spotting') }}</p>
        <v-progress-circular v-if="!isDataRequested" indeterminate color="primary" class="ma-4"></v-progress-circular>
        <v-radio-group v-else v-model="spottingModel" hide-details density="compact" class="py-1 mb-5">
          <v-radio
            v-for="(item, i) in spottingTypes"
            :key="i"
            :label="item.displayName"
            :value="item.id"
            :disabled="item.disabled"
            hide-details
            class="pr-2 bodyTextsSmall"></v-radio>
        </v-radio-group>
      </v-col>
      <v-col cols="12" class="px-0">
        <p>{{ $t('domain.fields.comment') }}</p>
        <v-textarea v-model="comment" counter="120" no-resize clearable />
      </v-col>
    </v-row>
  </v-container>
  <v-container fluid class="action-buttons" style="width: 100% !important">
    <v-row>
      <v-col class="pa-1">
        <v-btn block color="rgb(var(--v-theme-background))" variant="flat" @click="closeNewObservation()">
          {{ $t('common.actions.cancel') }}
        </v-btn>
      </v-col>
      <v-col class="pa-1">
        <v-btn
          block
          color="rgb(var(--v-theme-primary))"
          variant="flat"
          :disabled="!canSave"
          :loading="loading"
          @click="saveNewObservation()">
          {{ $t('common.actions.save') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts" setup>
// Frameworks Import
import type { Ref } from 'vue'
import { ref, onMounted, onUnmounted, watchEffect, computed, watch } from 'vue'

import { storeToRefs } from 'pinia'

import { format, parse } from 'date-fns'

// Radon import
import useView from '@tsyl/karonte/lib/composables/useView'
import type { Coordinates } from '@tsyl/karonte/lib/types/coordinates'
import type { RGeocoderResult } from '@tsyl/radon/lib/types/geocoder'

// Karonte import

// Fobs store
import { saveObservation } from '@/services/api/fobs'
import FobsStore from '@/stores/fobsStore'
import IncidentStore from '@/stores/incidentStore'
import MarkerStore from '@/stores/markerStore'
import SettingsStore from '@/stores/settingsStore'
import type { IncidentFobType, ObservationPreset, SpottingType, SpreadType } from '@/types/Fobs'
import { displayToIsoDate, isoToDisplayDate } from '@/utils/dateInput'
import emitter from '@/utils/EventBus'

const { getReactiveView } = useView()

const fobsStore = FobsStore()
const { spreadTypes, spottingTypes, isDataRequested } = storeToRefs(fobsStore)

const googleKey: string = import.meta.env.VITE_GOOGLE_API_KEY
const view = getReactiveView()

const data: Ref<IncidentFobType> = ref({} as IncidentFobType)
const comment: Ref<string> = ref('')

const surfaceSpreadModel: Ref<string | undefined> = ref(undefined)
const spottingModel: Ref<string | undefined> = ref(undefined)

const emits = defineEmits<{
  (event: 'closeNewObservationEvent'): void
}>()

const closeNewObservation = () => {
  emits('closeNewObservationEvent')
}

const loading: Ref<boolean> = ref<boolean>(false)

const saveNewObservation = async () => {
  try {
    loading.value = true
    await saveObservation(data.value)
    emitter.emit('save')
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
    closeNewObservation()
  }
}

const clickMarker = ref<boolean>(false)
const markerStore = MarkerStore()

const search = ref<RGeocoderResult>()
const geocoderResult = ref<RGeocoderResult>()

// `dateText` is what the field holds (tenant format or the native ISO value); `date` is the ISO day sent to the API
const { tenantDateFormat, lang } = storeToRefs(SettingsStore())
const calendarLocale = computed(() => lang.value.split('-')[0])
const dateText = ref<string>('')
// Calendar of the tenant-format field: it picks a Date, the field keeps the typed text
const pickerOpen = ref(false)
const pickerDate = computed<Date | undefined>({
  get: () => (date.value ? parse(date.value, 'yyyy-MM-dd', new Date()) : undefined),
  set: (value) => {
    if (value && tenantDateFormat.value) dateText.value = isoToDisplayDate(format(value, 'yyyy-MM-dd'), tenantDateFormat.value)
  }
})
const date = computed(() =>
  tenantDateFormat.value ? displayToIsoDate(dateText.value, tenantDateFormat.value) : dateText.value || undefined
)
const time = ref<string>()

const { markerCoordinates, enableMarker } = storeToRefs(markerStore)
enableMarker.value = true

watch(search, (val) => {
  if (!val) markerCoordinates.value = {}
  clickMarker.value = false
})

watch(geocoderResult, (val) => {
  if (val && !clickMarker.value) goToExtent()
  search.value = val
})

async function mapClickCallback() {
  clickMarker.value = true
}

watch(markerCoordinates, (marker) => {
  data.value.latitude = marker.latitude as number
  data.value.longitude = marker.longitude as number
})

function goToExtent() {
  const coordinates: Coordinates = {
    latitude: markerCoordinates.value.latitude as number,
    longitude: markerCoordinates.value.longitude as number
  }
  view.value?.goTo(coordinates, 15)
}

const canSave = computed(() => {
  return data.value.observationPreset && data.value.created_at && data.value.incident_id && data.value.latitude && data.value.longitude
})

// Sync on every model change: the clear button only updates the model (no input event) and a debounce could drop the last keystrokes on save
watch(comment, (value) => {
  data.value.comment = value
})

// HANDLE MOUNT / DISMOUNT
onMounted(() => {
  const incidentId = IncidentStore().incident?.id
  if (incidentId) data.value.incident_id = incidentId
  view.value?.addCallback('click', mapClickCallback)
  spreadTypes.value.forEach((x: SpreadType) => (x.disabled = false))
  spottingTypes.value.forEach((x: SpottingType) => (x.disabled = false))
})

onUnmounted(() => {
  enableMarker.value = false
  view.value?.removeCallback('click', mapClickCallback)
})

// HANDLE FOBS OPTIONS
function updateOptions(availableElements: Array<string>, types: Array<SpreadType | SpottingType>) {
  types.forEach((element: SpreadType | SpottingType) => {
    element.disabled = !availableElements.includes(element.id)
  })

  // set preset
  if (surfaceSpreadModel.value === undefined || spottingModel.value === undefined) return
  const observationPreset: ObservationPreset | undefined = fobsStore.getPreset(surfaceSpreadModel.value, spottingModel.value)
  if (observationPreset) data.value.observationPreset = observationPreset
}

watch(surfaceSpreadModel, (value) => {
  const availableSpots: Array<string> = fobsStore.getSpotsBasedOnSpread(value)
  updateOptions(availableSpots, spottingTypes.value)
})
watch(spottingModel, (value) => {
  const availableSpreads: Array<string> = fobsStore.getSpreadsBasedOnSpot(value)
  updateOptions(availableSpreads, spreadTypes.value)
})

// HANDLE DATETIME
watchEffect(() => (data.value.created_at = date.value && time.value ? `${date.value}T${time.value}` : ''))
</script>

<style scoped lang="scss">
:deep(.v-field__prepend-inner) {
  color: rgb(var(--v-theme-lighten));
}

:deep(.v-field__field) {
  color: rgb(var(--v-theme-lighten));
}
:deep(.v-field__append-inner) {
  color: rgb(var(--v-theme-lighten));
}

:deep(.v-label) {
  font-size: 14px;
}

:deep(.mdi-target) {
  padding-right: 14px;
}
:deep(.mdi-close-circle) {
  margin-right: 8px;
}

:deep(.customInput) {
  width: inherit;
}

.action-buttons {
  bottom: 0;
  width: inherit;
  box-shadow: 0px -1px 0px rgb(var(--v-theme-background));
}

::v-deep(.v-selection-control__wrapper) {
  margin-right: 1em;
}

.spreadColors::v-deep(.v-selection-control__wrapper) {
  margin-right: 1em;
  color: var(--spread-radio-color, #000000);
}
</style>
